// src/pages/secret/SecretPoliciesPage.tsx
// Access policies — path-based capability rules (Vault-compatible). CRUD.
// See docs/ux/secret-ia.md section 3. Admin-gated (AdminRoute, in App.tsx).

import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useI18n, useSecretSecretPolicy } from '@react/hooks';
import { Button, Input, Textarea, Card, CardHeader, CardTitle, CardContent, CardFooter, ErrorAlert } from '@react/components';
import { TypedConfirmDialog } from '@/features/secret/TypedConfirmDialog';
import type { SecretPolicy, SecretPolicyRule, SecretCapability } from '@core/types/secret';
import { SecretCapabilityValues } from '@core/types/secret';

const CAPABILITIES = SecretCapabilityValues.filter((v) => v !== 'SECRET_CAPABILITY_UNSPECIFIED');

interface RuleForm { path: string; capabilities: SecretCapability[] }

function capabilityLabel(c: SecretCapability): string {
  return c.replace('SECRET_CAPABILITY_', '').toLowerCase();
}

function PolicyForm({ initial, onSubmit, onCancel, isSaving, submitLabel }: {
  initial?: { name: string; description: string; rules: RuleForm[] };
  onSubmit: (data: { name: string; description: string; rules: RuleForm[] }) => void;
  onCancel: () => void;
  isSaving: boolean;
  submitLabel: string;
}) {
  const { t } = useI18n();
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [rules, setRules] = useState<RuleForm[]>(initial?.rules ?? [{ path: '', capabilities: ['SECRET_CAPABILITY_READ'] }]);

  const updateRule = (idx: number, field: keyof RuleForm, value: RuleForm[keyof RuleForm]) => {
    setRules((prev) => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };
  const toggleCapability = (idx: number, cap: SecretCapability) => {
    setRules((prev) => prev.map((r, i) => {
      if (i !== idx) return r;
      const has = r.capabilities.includes(cap);
      return { ...r, capabilities: has ? r.capabilities.filter((c) => c !== cap) : [...r.capabilities, cap] };
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSubmit({ name: name.trim(), description: description.trim(), rules: rules.filter((r) => r.path.trim()) });
  };

  return (
    <Card className="border-primary/40">
      <form onSubmit={handleSubmit}>
        <CardHeader><CardTitle>{submitLabel}</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <Input label={t('secret.policy.field.name')} value={name} onChange={(e) => setName(e.target.value)} required disabled={!!initial} />
          <Textarea label={t('secret.policy.field.description')} value={description} onChange={(e) => setDescription(e.target.value)} />

          <div className="space-y-2">
            <span className="label">{t('secret.policy.field.rules')}</span>
            {rules.map((rule, idx) => (
              <div key={idx} className="rounded-md border border-border p-3 space-y-2">
                <Input
                  value={rule.path}
                  onChange={(e) => updateRule(idx, 'path', e.target.value)}
                  placeholder="secret/data/my-app/*"
                />
                <div className="flex flex-wrap gap-2">
                  {CAPABILITIES.map((cap) => (
                    <button
                      key={cap}
                      type="button"
                      onClick={() => toggleCapability(idx, cap)}
                      className={`px-2.5 py-1 rounded-md text-xs font-medium border ${rule.capabilities.includes(cap) ? 'bg-primary text-primary-foreground border-primary' : 'bg-background text-muted-foreground border-border hover:border-foreground/20'}`}
                    >
                      {capabilityLabel(cap)}
                    </button>
                  ))}
                </div>
                <Button type="button" variant="ghost" size="sm" onClick={() => setRules((prev) => prev.filter((_, i) => i !== idx))}>{t('common.remove')}</Button>
              </div>
            ))}
            <Button type="button" variant="secondary" size="sm" onClick={() => setRules((prev) => [...prev, { path: '', capabilities: ['SECRET_CAPABILITY_READ'] }])}>
              {t('secret.policy.addRule')}
            </Button>
          </div>
        </CardContent>
        <CardFooter className="flex justify-end gap-2">
          <Button type="button" variant="secondary" size="sm" onClick={onCancel} disabled={isSaving}>{t('common.cancel')}</Button>
          <Button type="submit" size="sm" disabled={isSaving}>{isSaving ? t('common.saving') : submitLabel}</Button>
        </CardFooter>
      </form>
    </Card>
  );
}

export const SecretPoliciesPage = observer(() => {
  const { t } = useI18n();
  const store = useSecretSecretPolicy();
  const [policies, setPolicies] = useState<SecretPolicy[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editTarget, setEditTarget] = useState<SecretPolicy | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SecretPolicy | null>(null);
  const [isActing, setIsActing] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    store.ListPolicies().then((r) => setPolicies(r.policies ?? [])).catch(() => setPolicies([])).finally(() => setLoading(false));
  }, [store]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (data: { name: string; description: string; rules: RuleForm[] }) => {
    setIsActing(true);
    try {
      await store.CreatePolicy({
        policy: {
          name: data.name,
          description: data.description,
          rules: data.rules.map((r) => ({ path: r.path, capabilities: r.capabilities, allowed_parameters: [], denied_parameters: [] } as SecretPolicyRule)),
        } as SecretPolicy,
      });
      setShowCreate(false);
      load();
    } finally {
      setIsActing(false);
    }
  };

  const handleUpdate = async (data: { name: string; description: string; rules: RuleForm[] }) => {
    if (!editTarget) return;
    setIsActing(true);
    try {
      await store.UpdatePolicy(editTarget.name, {
        policy_name: editTarget.name,
        policy: {
          name: editTarget.name,
          description: data.description,
          rules: data.rules.map((r) => ({ path: r.path, capabilities: r.capabilities, allowed_parameters: [], denied_parameters: [] } as SecretPolicyRule)),
        } as SecretPolicy,
      });
      setEditTarget(null);
      load();
    } finally {
      setIsActing(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsActing(true);
    try { await store.DeletePolicy(deleteTarget.name); setDeleteTarget(null); load(); } finally { setIsActing(false); }
  };

  return (
    <div className="space-y-6 p-6">
      {/* The tab already names this page; the heading stays for screen readers. */}
      <div className="flex items-center justify-between">
        <h1 className="sr-only">{t('secret.policy.title')}</h1>
        <div className="ml-auto flex items-center gap-3">
          <Link to="/secrets" className="text-sm text-primary hover:underline">{t('secret.console.backToConsole')}</Link>
          {!showCreate && <Button onClick={() => setShowCreate(true)}>{t('secret.policy.create')}</Button>}
        </div>
      </div>

      {store.error != null && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}

      {showCreate && (
        <PolicyForm submitLabel={t('secret.policy.create')} isSaving={isActing} onSubmit={handleCreate} onCancel={() => setShowCreate(false)} />
      )}

      {editTarget && (
        <PolicyForm
          submitLabel={t('secret.policy.editTitle')}
          isSaving={isActing}
          initial={{
            name: editTarget.name,
            description: editTarget.description,
            rules: editTarget.rules.map((r) => ({ path: r.path, capabilities: r.capabilities })),
          }}
          onSubmit={handleUpdate}
          onCancel={() => setEditTarget(null)}
        />
      )}

      <TypedConfirmDialog
        open={!!deleteTarget}
        title={t('secret.policy.deleteTitle')}
        description={t('secret.policy.deleteDescription')}
        phrase={deleteTarget?.name ?? ''}
        confirmLabel={t('common.delete')}
        isConfirming={isActing}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      {loading ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : policies.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('secret.policy.empty')}</p>
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border">
          {policies.map((policy) => (
            <li key={policy.name} className="flex items-center justify-between p-3">
              <div>
                <p className="text-sm font-medium text-foreground">{policy.name}</p>
                <p className="text-xs text-muted-foreground">{policy.rules.length} {t('secret.policy.rulesCount')}</p>
              </div>
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    // GetPolicy — the authoritative single-policy read, in case the list row is stale.
                    store.GetPolicy(policy.name).then(setEditTarget).catch(() => setEditTarget(policy));
                  }}
                >
                  {t('common.edit')}
                </Button>
                <Button variant="ghost" size="sm" className="text-destructive" onClick={() => setDeleteTarget(policy)}>{t('common.delete')}</Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
});

export default SecretPoliciesPage;
